<template>
  <div class="admin-shell" :class="{ 'admin-shell--mobile-app': isMobileApp }">
    <div v-if="!loading" class="admin-shell__workspace">
      <toolbar v-if="!isEmbeddedApp" class="no-print" :live-title="liveTitle" />

      <navbar v-if="!isMobileApp && !isEmbeddedApp" />
      <!-- <HelpPanel></HelpPanel> -->
      <v-main class="admin-shell__main" :class="{ 'admin-shell__main--live': showLivePortal }">
        <v-container fluid class="admin-shell__content" :class="{ 'admin-shell__content--live': showLivePortal }">
          <v-alert
            v-if="supportSession"
            type="warning"
            prominent
            outlined
            class="mb-4"
            role="status"
          >
            <div class="d-flex flex-column flex-sm-row align-start align-sm-center">
              <div>
                <div class="font-weight-bold">Audited support mode</div>
                <div class="text-body-2">You are viewing this customer account temporarily. Access expires {{ supportExpiryLabel }}.</div>
              </div>
              <v-spacer />
              <v-btn color="warning" min-height="44" class="mt-3 mt-sm-0" :loading="endingSupport" @click="endSupportSession">
                End support session
              </v-btn>
            </div>
          </v-alert>
          <!-- <v-card
            class="border-r-0"
            color="red darken-4"
            dark
            v-if="!showLivePortal && !isEmbeddedApp && !currentUser.verified && !closeNotVerified"
          >
            <v-card-text >
              <v-layout class="ma-2 white--text" align-space-around justify-space-around row fill-height>
                <v-layout column>
                  <h1 class="headline font-weight-regular">
                    Email is not Verified
                  </h1>
                  <h2 class="subheading font-weight-regular">
                    some of the features will not work until you verify email.
                  </h2>
                </v-layout>
                <v-spacer />
                <v-btn outlined rounded @click="resendVerify">Send Again</v-btn>
              </v-layout>
            </v-card-text>
          </v-card> -->
          <v-alert
            v-if="!showLivePortal && !isEmbeddedApp && !currentUser.verified && !closeNotVerified"
            border="left"
            colored-border
            color="primary"
            icon="mdi-email-alert"
            prominent
            class="rounded-xl elevation-0 pa-4 pa-sm-5 mb-6"
          >
            <div
              class="d-flex flex-column flex-sm-row align-start align-sm-center justify-space-between"
            >
              <div>
                  <h2 class="text-h6 font-weight-bold mb-2">
                  Verify your email address
                </h2>
                <p class="subtitle-2 mb-3">
                  We’ve sent a verification link to
                  <strong>{{ currentUser.email }}</strong
                  >. You’ll need to verify your email to unlock all features.
                  <br />
                  (We promise it’s not one of those boring confirmation emails
                  😉)
                </p>
              </div>

              <div class="text-center mt-2 mt-sm-0">
                <v-btn
                  color="primary"
                  rounded
                  class="px-6"
                  @click="resendVerify"
                >
                  <v-icon left>mdi-email-send-outline</v-icon>
                  Resend Link
                </v-btn>

                <v-btn
                  text
                  class="ml-sm-2"
                  @click="closeNotVerified = true"
                >
                  Dismiss
                </v-btn>
              </div>
            </div>
          </v-alert>
          <live-portal-frame v-if="showLivePortal" v-show="!showLocalNotifications" :path="$route.fullPath" @page-change="onLivePageChange" />
          <div v-if="showLocalNotifications" class="admin-shell__local-content"><router-view /></div>
          <router-view v-else-if="!showLivePortal" />
        </v-container>
      </v-main>
      <mobile-navigation v-if="isMobileApp" :current-path="showLocalNotifications ? '/notifications' : livePath" />
    </div>
    <div v-else>
      <v-dialog v-model="loading" hide-overlay persistent width="300">
        <v-card color="primary" dark>
          <v-container grid-list-lg class="px-5">
            <div class="mt-2">Please Stand By</div>
            <v-progress-linear
              indeterminate
              color="white"
              class="mb-5"
            ></v-progress-linear>
          </v-container>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import RegisterStoreModule from "@/mixins/RegisterStoreModule";
import settingStore from "@/modules/Settings/store.js";
import tasksStore from "@/modules/Tasks/store.js";
import Navbar from "@/modules/Layout/components/NavigationPanel.vue";
import Toolbar from "@/modules/Layout/components/Toolbar.vue";
import integrationStore from "../Integration/store.js";
import layoutStore from "./store";
import backofficeService from "../../api/backoffice.js";
import signUpService from "../SignUp/service.js";
import MobileNavigation from "@/modules/Layout/components/MobileNavigation.vue";
import LivePortalFrame from "@/mobile/LivePortalFrame.vue";
import { embeddedMobileApp, isNativeMobileApp, useLiveMobilePortal } from "@/mobile/runtime";
import { refreshMobilePushRegistration } from "@/mobile/service";
import authService from "@/modules/Authentication/service";
import { clearSupportSession, getSupportSession, getSupportToken } from "@/modules/Authentication/supportSession";
import { mobileBrand } from "@/mobile/brand";

export default {
  name: "layout",
  mixins: [RegisterStoreModule],
  async mounted() {
    console.log("Layout mounted");
    try {
      this.registerStoreModule("layout", layoutStore);
      await this.registerStoreModule("integration", integrationStore);
      await this.registerStoreModule("settings", settingStore);
      await this.registerStoreModule("tasks", tasksStore);
      this.supportSession = getSupportSession();
      this.isSupportContext = Boolean(this.supportSession);
      window.addEventListener("repairnest:support-unauthorized", this.onSupportUnauthorized);
      if (this.supportSession && new Date(this.supportSession.expiresAt).getTime() <= Date.now()) {
        this.finishSupportSession();
        return;
      }
      await this.checkToken();
      if (!this.authenticated) {
        if (this.isSupportContext) this.finishSupportSession();
        else this.$router.replace({ path: "/login", query: { reason: "session-expired" } }).catch(() => {});
        return;
      }
      await this.$store.dispatch("global/initUser");
      await this.$store.dispatch("global/initOrganization").catch((error) => {
        console.log("Error in init organization", error);
        console.log("this.currentUser", this.currentUser);
        if (this.currentUser.organizationCount === 0) {
          this.$router.push("/newOrganization");
        } else {
          this.$router.push({ name: "authentication-select-organization" });
        }
      });
      await this.$store.dispatch("integration/setIntegrations");
      await this.$store.dispatch("integration/setLocationIntegrations");
    } catch (error) {
      // Startup requests must never strand the embedded portal behind a blank
      // loading screen. A rejected token has already been removed by the API
      // interceptor; the router handoff tells the native shell to show login.
      if (!localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}token`)) {
        this.authenticated = false;
        this.$router.replace({ path: "/login", query: { reason: "session-expired" } }).catch(() => {});
      } else {
        console.error("Unable to finish loading the back office", error); // eslint-disable-line no-console
      }
    } finally {
      this.loading = false;
    }
    if (!this.authenticated) return;
    await refreshMobilePushRegistration().catch((error) => {
      console.error("Unable to refresh mobile notification registration", error); // eslint-disable-line no-console
    });
    await this.$store.dispatch("tasks/initTasks");
    this.pollData();
  },
  components: {
    Navbar,
    Toolbar,
    MobileNavigation,
    LivePortalFrame,
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
    ...mapGetters("brand", ["currentBrand"]),
    supportExpiryLabel() {
      if (!this.supportSession) return "soon";
      return this.$moment(this.supportSession.expiresAt).format("h:mm A");
    },
    isMobileApp() {
      return isNativeMobileApp;
    },
    isEmbeddedApp() { return embeddedMobileApp.active; },
    showLivePortal() { return useLiveMobilePortal; },
    showLocalNotifications() { return useLiveMobilePortal && this.$route.path === "/notifications"; },
    liveTitle() {
      if (!this.showLivePortal) return "";
      const path = this.livePath.split("?")[0];
      if (path === "/" || path.startsWith("/home")) return "Overview";
      if (path.startsWith("/dayEnd")) return "Day End";
      if (path.startsWith("/location")) return "Locations";
      if (path.startsWith("/employee")) return "Employees";
      if (path.startsWith("/notifications")) return "Notifications";
      if (path.startsWith("/settings")) return "Settings";
      if (path.startsWith("/report")) return "Reports";
      if (this.isMobileApp) return mobileBrand.name;
      return { repairnest: "RepairNest", storesync: "StoreSync POS", autorepair: "Auto Repair Desk" }[this.currentBrand] || "Back office";
    },
  },
  props: {
    source: String,
  },
  beforeDestroy() {
    clearInterval(this.polling);
    window.removeEventListener("repairnest:support-unauthorized", this.onSupportUnauthorized);
  },
  data() {
    return {
      closeNotVerified: false,
      loading: true,
      authenticated: true,
      polling: null,
      livePath: this.$route.fullPath,
      supportSession: null,
      endingSupport: false,
      isSupportContext: false,
      supportContextEnding: false,
    };
  },
  watch: {
    "$route.fullPath"(path) { this.livePath = path; },
    authenticated(val) {
      if (!val && !this.isSupportContext) {
        this.$router.push("/login");
      }
    },
  },
  methods: {
    onLivePageChange(path) { this.livePath = path; },
    async checkToken() {
      this.authenticated = await backofficeService.valid();
    },
    pollData() {
      this.polling = setInterval(() => {
        if (this.supportSession && new Date(this.supportSession.expiresAt).getTime() <= Date.now()) {
          this.finishSupportSession();
          return;
        }
        if (
          !getSupportToken() && !localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}token`)
        ) {
          this.authenticated = false;
        }
      }, 1000);
    },
    finishSupportSession() {
      if (this.supportContextEnding) return;
      this.supportContextEnding = true;
      clearSupportSession();
      this.supportSession = null;
      if (window.opener) window.close();
      if (!window.closed) this.$router.replace("/support-ended");
    },
    onSupportUnauthorized() {
      if (this.isSupportContext) this.finishSupportSession();
    },
    async endSupportSession() {
      if (this.endingSupport) return;
      this.endingSupport = true;
      try {
        await authService.endSupportAccess();
      } catch (error) {
        // Local credential removal still ends access on this browser if offline.
      } finally {
        this.endingSupport = false;
        this.finishSupportSession();
      }
    },
    resendVerify() {
      signUpService
        .resendVerifyEmail({
          email: this.currentUser.email,
          appSource: "storesyncpos",
        })
        .then((response) => {
          this.$Progress.finish();
          this.closeNotVerified = true;
        })
        .catch((error) => {
          this.$Progress.fail();
          this.isLoading = false;
          this.error = true;
          this.errorMessage = error.data.message;
        });
    },
  },
};
</script>
<style>
.desktop-view-container {
  height: calc(100vh - 58px);
  width: 100%;
}
.touch-view-container {
  height: calc(100vh - 58px);
  /* height: calc((calc(var(--vh, 1vh) * 100)) - 58px); */
  /* height: calc(var(--vh, 1vh) * 100); */
  width: 100%;
}
.full-width {
  width: 100%;
}
/* Allow v-select items to grow vertically and limit the horizontal grow*/
.v-menu__content > .v-select-list > .v-list > div > a {
  min-height: 48px !important;
  height: auto !important;
  max-width: 600px;
}
/* Limits the size of a td within a datatable */
.small-td {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Limits the size of a td within a datatable */
.medium-td {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Limits the size of a td within a datatable */
.large-td {
  max-width: 500px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
